// v10/src/components/wb/WBCalculator.js
//
// W&B parte 1 — Pantalla full-screen para calcular peso y balance de un vuelo.
// Pattern: state machine en App.js (mismo patrón que PilotTurnos / PilotFlightLog).
// Props: { darkMode, currentUser, onClose }

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Calculator, AlertCircle, Loader, Plane, Info,
  Save, ClipboardList,
} from 'lucide-react';
import api from '../../services/apiService';
import wbService from '../../services/wbService';
import CGEnvelopeChart from './CGEnvelopeChart';
import WBResultPanel from './WBResultPanel';
import SaveWBModal from './SaveWBModal';
import WBHistoryModal from './WBHistoryModal';
import { calculateWeightBalance } from '../../utils/wbCalculator';

const KG_TO_LB = 2.20462;
const L_TO_GAL = 0.264172;
const GAL_TO_L = 3.7854118;

const WBCalculator = ({ darkMode, currentUser, onClose }) => {
  const { t } = useTranslation();

  const text   = darkMode ? 'white' : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const glassBg = darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)';

  // ─── State ────────────────────────────────────────────────────────
  const [aircraftList, setAircraftList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [errorList, setErrorList] = useState(null);

  const [selectedAircraftId, setSelectedAircraftId] = useState('');
  const [aircraftData, setAircraftData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [errorData, setErrorData] = useState(null);

  // Inputs del vuelo
  const defaultPilotWeightLb = useMemo(() => {
    const kg = Number(currentUser?.weight_kg);
    if (kg > 0) return round1(kg * KG_TO_LB);
    return round1(80 * KG_TO_LB); // 80 kg default
  }, [currentUser]);

  const [inputs, setInputs] = useState({
    pilot_weight_lb: defaultPilotWeightLb,
    copilot_weight_lb: 0,
    passengers_rear_weight_lb: 0,
    fuel_amount: 0, // valor en la unidad seleccionada
    baggage_weight_lb: 0,
  });

  const [fuelUnit, setFuelUnit] = useState('L'); // 'L' o 'gal'

  const [result, setResult] = useState(null);
  const [errorCalc, setErrorCalc] = useState(null);

  // Modals (parte 2)
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [templateToast, setTemplateToast] = useState(false);

  // ─── Cargar lista de aeronaves ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingList(true);
      setErrorList(null);
      try {
        const res = await api.get('/api/aircraft', { params: { is_active: 'true' } });
        if (cancelled) return;
        const list = res.data?.data || [];
        setAircraftList(list);
      } catch (err) {
        if (!cancelled) {
          setErrorList(
            err.response?.data?.message ||
              t('wb_error_load_list', { ns: 'admin' })
          );
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [t]);

  // ─── Cargar datos W&B de la aeronave seleccionada ─────────────────
  useEffect(() => {
    let cancelled = false;
    if (!selectedAircraftId) {
      setAircraftData(null);
      setResult(null);
      setErrorData(null);
      return;
    }
    async function load() {
      setLoadingData(true);
      setErrorData(null);
      setResult(null);
      try {
        const r = await wbService.getAircraftWbData(selectedAircraftId);
        if (cancelled) return;
        setAircraftData(r.data);
      } catch (err) {
        if (!cancelled) {
          setErrorData(
            err.response?.data?.message ||
              t('wb_error_load_aircraft', { ns: 'admin' })
          );
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedAircraftId, t]);

  // ─── Helpers ──────────────────────────────────────────────────────
  const updateInput = (key, value) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const onNumberInput = (key, raw) => {
    if (raw === '' || raw === '-') {
      updateInput(key, raw);
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    updateInput(key, n);
  };

  const numericValue = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const toggleFuelUnit = () => {
    setFuelUnit(prev => {
      const next = prev === 'L' ? 'gal' : 'L';
      // Convertir el valor para mantener la cantidad
      const current = numericValue(inputs.fuel_amount);
      const converted = prev === 'L' ? current * L_TO_GAL : current * GAL_TO_L;
      setInputs(p => ({ ...p, fuel_amount: round1(converted) }));
      return next;
    });
  };

  // Validaciones inline
  const validation = useMemo(() => {
    const errs = {};
    const pilot = numericValue(inputs.pilot_weight_lb);
    if (pilot < 50) errs.pilot_weight_lb = t('wb_validation_pilot_min', { ns: 'admin' });
    if (numericValue(inputs.copilot_weight_lb) < 0) errs.copilot_weight_lb = t('wb_validation_negative', { ns: 'admin' });
    if (numericValue(inputs.passengers_rear_weight_lb) < 0) errs.passengers_rear_weight_lb = t('wb_validation_negative', { ns: 'admin' });
    if (numericValue(inputs.fuel_amount) < 0) errs.fuel_amount = t('wb_validation_negative', { ns: 'admin' });
    if (numericValue(inputs.baggage_weight_lb) < 0) errs.baggage_weight_lb = t('wb_validation_negative', { ns: 'admin' });
    return errs;
  }, [inputs, t]);

  // Convierte los inputs del form al shape que espera wbCalculator
  // (fuel siempre en litros, sin importar la unidad seleccionada en la UI).
  const apiInputs = useMemo(() => {
    const fuelAmount = numericValue(inputs.fuel_amount);
    const fuelLiters = fuelUnit === 'gal' ? fuelAmount * GAL_TO_L : fuelAmount;
    return {
      pilot_weight_lb: numericValue(inputs.pilot_weight_lb),
      copilot_weight_lb: numericValue(inputs.copilot_weight_lb),
      passengers_rear_weight_lb: numericValue(inputs.passengers_rear_weight_lb),
      fuel_liters: fuelLiters,
      baggage_weight_lb: numericValue(inputs.baggage_weight_lb),
    };
  }, [inputs, fuelUnit]);

  // ─── Cálculo en VIVO (parte 2) ───────────────────────────────────
  // Sin debounce, sin request al backend, sin loading. Cualquier cambio en
  // inputs o aircraftData dispara recálculo instantáneo.
  // El backend RECALCULA al guardar (POST /api/wb/save) — defense in depth.
  useEffect(() => {
    if (!aircraftData || !aircraftData.wb_data_configured) {
      setResult(null);
      setErrorCalc(null);
      return;
    }
    if (Object.keys(validation).length > 0) {
      // Hay inputs inválidos — no calculamos pero NO limpiamos el último
      // resultado válido (para que el gráfico no parpadee).
      return;
    }
    try {
      const r = calculateWeightBalance(
        {
          empty_weight_lb: aircraftData.aircraft.empty_weight_lb,
          empty_weight_arm_in: aircraftData.aircraft.empty_weight_arm_in,
          max_weight_kg: aircraftData.aircraft.max_weight_kg,
          cg_envelope_points: aircraftData.aircraft.cg_envelope_points,
          wb_arms: aircraftData.aircraft.wb_arms,
          has_rear_seats: aircraftData.aircraft.has_rear_seats,
        },
        apiInputs
      );
      setResult(r);
      setErrorCalc(null);
    } catch (err) {
      setErrorCalc(err.message || t('wb_calculate_error', { ns: 'admin' }));
      setResult(null);
    }
    // eslint-disable-next-line
  }, [apiInputs, aircraftData]);

  // ─── Handlers parte 2 ─────────────────────────────────────────────
  const handleLoadTemplate = (loadedInputs) => {
    if (!loadedInputs) return;
    // Los inputs persistidos están en LITROS (campo fuel_liters).
    // Reconvertir a la unidad actual del form.
    const fuelLitersIn = numericValue(loadedInputs.fuel_liters);
    const fuelInUnit = fuelUnit === 'gal' ? fuelLitersIn * L_TO_GAL : fuelLitersIn;

    setInputs({
      pilot_weight_lb: round1(numericValue(loadedInputs.pilot_weight_lb)),
      copilot_weight_lb: round1(numericValue(loadedInputs.copilot_weight_lb)),
      passengers_rear_weight_lb: round1(numericValue(loadedInputs.passengers_rear_weight_lb)),
      fuel_amount: round1(fuelInUnit),
      baggage_weight_lb: round1(numericValue(loadedInputs.baggage_weight_lb)),
    });
    setTemplateToast(true);
    setTimeout(() => setTemplateToast(false), 2200);
  };

  // ─── Render ───────────────────────────────────────────────────────
  const hasRearSeats = !!aircraftData?.aircraft?.has_rear_seats;
  const wbConfigured = !!aircraftData?.wb_data_configured;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: darkMode ? '#0a0f1e' : '#f1f5f9',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: darkMode ? 'rgba(10,15,30,0.85)' : 'rgba(241,245,249,0.85)',
          backdropFilter: 'blur(20px)',
          borderBottom: border,
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <motion.button
          onClick={onClose}
          whileTap={{ scale: 0.92 }}
          style={{
            width: 40, height: 40, borderRadius: 12,
            border, background: glassBg,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: sub, backdropFilter: 'blur(8px)',
          }}
        >
          <ArrowLeft style={{ width: 18, height: 18 }} />
        </motion.button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 32, height: 32, borderRadius: 10,
                background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 3px 12px rgba(99,102,241,0.35)',
                flexShrink: 0,
              }}
            >
              <Calculator style={{ width: 16, height: 16, color: 'white' }} />
            </div>
            <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: text }}>
              {t('wb_calculator_title', { ns: 'admin' })}
            </p>
          </div>
          <p style={{ margin: '2px 0 0 40px', fontSize: 12, color: sub }}>
            {t('wb_calculator_subtitle', { ns: 'admin' })}
          </p>
        </div>

        {/* Botones parte 2 — Histórico + Guardar */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <motion.button
            onClick={() => setShowHistoryModal(true)}
            whileTap={{ scale: 0.94 }}
            title={t('wb_view_history', { ns: 'admin' })}
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              border,
              background: glassBg,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: text,
              fontSize: 12,
              fontWeight: 700,
              backdropFilter: 'blur(8px)',
            }}
          >
            <ClipboardList style={{ width: 14, height: 14 }} />
            <span className="wb-btn-label">{t('wb_view_history', { ns: 'admin' })}</span>
          </motion.button>
          <motion.button
            onClick={() => setShowSaveModal(true)}
            disabled={!result || !aircraftData?.wb_data_configured}
            whileTap={(result && aircraftData?.wb_data_configured) ? { scale: 0.94 } : undefined}
            title={t('wb_save_button', { ns: 'admin' })}
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              border: 'none',
              background: (result && aircraftData?.wb_data_configured)
                ? 'linear-gradient(135deg,#6366f1,#8b5cf6)'
                : (darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)'),
              color: (result && aircraftData?.wb_data_configured) ? 'white' : sub,
              cursor: (result && aircraftData?.wb_data_configured) ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              boxShadow: (result && aircraftData?.wb_data_configured)
                ? '0 4px 14px rgba(99,102,241,0.35)'
                : 'none',
            }}
          >
            <Save style={{ width: 14, height: 14 }} />
            <span className="wb-btn-label">{t('wb_save_button', { ns: 'admin' })}</span>
          </motion.button>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px 16px 40px' }}>

        {/* Disclaimer referencial — no descartable */}
        <div
          role="note"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            background: darkMode ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 16,
            color: darkMode ? '#fde68a' : '#92400e',
            fontSize: 12.5,
            lineHeight: 1.45,
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">⚠️</span>
          <span>{t('wb_disclaimer_referential', { ns: 'admin' })}</span>
        </div>

        {/* Selector de aeronave */}
        <div
          style={{
            background: cardBg,
            border,
            borderRadius: 16,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: 11,
              fontWeight: 600,
              color: sub,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              marginBottom: 8,
            }}
          >
            <Plane style={{ width: 12, height: 12, display: 'inline', marginRight: 6, verticalAlign: -1 }} />
            {t('wb_select_aircraft', { ns: 'admin' })}
          </label>

          {loadingList ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: sub, fontSize: 13 }}>
              <Loader style={{ width: 14, height: 14 }} className="spin" />
              {t('wb_loading_list', { ns: 'admin' })}
            </div>
          ) : errorList ? (
            <div style={{ color: '#ef4444', fontSize: 13 }}>{errorList}</div>
          ) : aircraftList.length === 0 ? (
            <div style={{ color: sub, fontSize: 13 }}>
              {t('wb_no_aircraft', { ns: 'admin' })}
            </div>
          ) : (
            <select
              value={selectedAircraftId}
              onChange={(e) => setSelectedAircraftId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 10,
                border,
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'white',
                color: text,
                fontSize: 14,
                outline: 'none',
              }}
            >
              <option value="">{t('wb_select_aircraft_placeholder', { ns: 'admin' })}</option>
              {aircraftList.map(a => (
                <option key={a.id} value={a.id}>
                  {[a.registration, `${a.manufacturer} ${a.model_name}`].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Cargando datos del avión */}
        {loadingData && (
          <div
            style={{
              padding: 16,
              borderRadius: 12,
              background: cardBg,
              border,
              color: sub,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}
          >
            <Loader className="spin" style={{ width: 14, height: 14 }} />
            {t('wb_loading_aircraft', { ns: 'admin' })}
          </div>
        )}

        {/* Error al cargar datos */}
        {errorData && (
          <div
            style={{
              padding: 14,
              borderRadius: 12,
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.2)',
              color: '#ef4444',
              fontSize: 13,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <AlertCircle style={{ width: 16, height: 16 }} />
            {errorData}
          </div>
        )}

        {/* Empty state — sin aeronave seleccionada */}
        {!selectedAircraftId && !loadingList && !errorList && aircraftList.length > 0 && (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: sub,
              fontSize: 14,
              background: cardBg,
              border,
              borderRadius: 16,
            }}
          >
            <Calculator style={{ width: 36, height: 36, opacity: 0.4, margin: '0 auto 12px' }} />
            <p style={{ margin: 0 }}>{t('wb_empty_state', { ns: 'admin' })}</p>
          </div>
        )}

        {/* Aeronave sin datos W&B configurados */}
        {aircraftData && !wbConfigured && (
          <div
            style={{
              padding: 18,
              borderRadius: 14,
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.25)',
              color: '#f59e0b',
              fontSize: 13,
              marginBottom: 16,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <Info style={{ width: 18, height: 18, flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {t('wb_aircraft_no_data', { ns: 'admin' })}
              </div>
              <div style={{ opacity: 0.85 }}>
                {t('wb_aircraft_no_data_help', { ns: 'admin' })}
              </div>
            </div>
          </div>
        )}

        {/* Formulario + Resultado — solo si hay aeronave con datos */}
        {aircraftData && wbConfigured && (
          <>
            <div
              style={{
                background: cardBg,
                border,
                borderRadius: 16,
                padding: 18,
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              {/* Sección Tripulación */}
              <Section title={t('wb_section_pilot', { ns: 'admin' })} darkMode={darkMode}>
                <NumField
                  label={t('wb_pilot_weight', { ns: 'admin' })}
                  unit="lb"
                  value={inputs.pilot_weight_lb}
                  onChange={(v) => onNumberInput('pilot_weight_lb', v)}
                  step={1}
                  min={50}
                  error={validation.pilot_weight_lb}
                  darkMode={darkMode}
                  onStep={(d) => updateInput('pilot_weight_lb', round1(numericValue(inputs.pilot_weight_lb) + d))}
                />
                <NumField
                  label={t('wb_copilot_weight', { ns: 'admin' })}
                  unit="lb"
                  value={inputs.copilot_weight_lb}
                  onChange={(v) => onNumberInput('copilot_weight_lb', v)}
                  step={1}
                  min={0}
                  error={validation.copilot_weight_lb}
                  darkMode={darkMode}
                  onStep={(d) => updateInput('copilot_weight_lb', Math.max(0, round1(numericValue(inputs.copilot_weight_lb) + d)))}
                />
                {hasRearSeats && (
                  <NumField
                    label={t('wb_rear_passengers', { ns: 'admin' })}
                    unit="lb"
                    value={inputs.passengers_rear_weight_lb}
                    onChange={(v) => onNumberInput('passengers_rear_weight_lb', v)}
                    step={1}
                    min={0}
                    error={validation.passengers_rear_weight_lb}
                    darkMode={darkMode}
                    onStep={(d) => updateInput('passengers_rear_weight_lb', Math.max(0, round1(numericValue(inputs.passengers_rear_weight_lb) + d)))}
                  />
                )}
              </Section>

              {/* Sección Combustible */}
              <Section title={t('wb_section_fuel', { ns: 'admin' })} darkMode={darkMode}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: sub, fontWeight: 600 }}>
                    {t('wb_fuel_unit', { ns: 'admin' })}:
                  </span>
                  <div
                    style={{
                      display: 'inline-flex',
                      borderRadius: 10,
                      border,
                      background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.04)',
                      padding: 2,
                    }}
                  >
                    {['L', 'gal'].map(u => (
                      <button
                        key={u}
                        onClick={() => fuelUnit !== u && toggleFuelUnit()}
                        style={{
                          padding: '6px 14px',
                          fontSize: 12,
                          fontWeight: 700,
                          borderRadius: 8,
                          border: 'none',
                          cursor: 'pointer',
                          background: fuelUnit === u
                            ? (darkMode ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.15)')
                            : 'transparent',
                          color: fuelUnit === u
                            ? '#6366f1'
                            : sub,
                        }}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <NumField
                  label={t('wb_fuel_amount', { ns: 'admin' })}
                  unit={fuelUnit}
                  value={inputs.fuel_amount}
                  onChange={(v) => onNumberInput('fuel_amount', v)}
                  step={fuelUnit === 'L' ? 5 : 1}
                  min={0}
                  error={validation.fuel_amount}
                  darkMode={darkMode}
                  onStep={(d) => updateInput('fuel_amount', Math.max(0, round1(numericValue(inputs.fuel_amount) + d)))}
                  hint={
                    aircraftData?.aircraft?.fuel_capacity_usable_liters
                      ? `${t('wb_fuel_max', { ns: 'admin' })}: ${formatFuelMax(aircraftData.aircraft.fuel_capacity_usable_liters, fuelUnit)} ${fuelUnit}`
                      : null
                  }
                />
              </Section>

              {/* Sección Equipaje */}
              <Section title={t('wb_section_baggage', { ns: 'admin' })} darkMode={darkMode}>
                <NumField
                  label={t('wb_baggage_weight', { ns: 'admin' })}
                  unit="lb"
                  value={inputs.baggage_weight_lb}
                  onChange={(v) => onNumberInput('baggage_weight_lb', v)}
                  step={1}
                  min={0}
                  error={validation.baggage_weight_lb}
                  darkMode={darkMode}
                  onStep={(d) => updateInput('baggage_weight_lb', Math.max(0, round1(numericValue(inputs.baggage_weight_lb) + d)))}
                />
              </Section>

              {/* Error de cálculo */}
              <AnimatePresence>
                {errorCalc && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    style={{
                      padding: 12,
                      borderRadius: 10,
                      background: 'rgba(239,68,68,0.08)',
                      border: '1px solid rgba(239,68,68,0.2)',
                      color: '#ef4444',
                      fontSize: 13,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <AlertCircle style={{ width: 16, height: 16 }} />
                    {errorCalc}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Panel de resultado */}
            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                  <WBResultPanel result={result} darkMode={darkMode} />

                  {/* Gráfico envelope */}
                  <div
                    style={{
                      background: cardBg,
                      border,
                      borderRadius: 16,
                      padding: 18,
                    }}
                  >
                    <p
                      style={{
                        margin: '0 0 12px',
                        fontSize: 13,
                        fontWeight: 700,
                        color: text,
                      }}
                    >
                      {t('wb_envelope_chart_title', { ns: 'admin' })}
                    </p>
                    <CGEnvelopeChart
                      envelopePoints={aircraftData.aircraft.cg_envelope_points}
                      flightPoint={{ weight_lb: result.total_weight_lb, cg_in: result.cg_in }}
                      withinEnvelope={result.within_envelope}
                      darkMode={darkMode}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>

      {/* Modal Guardar */}
      <AnimatePresence>
        {showSaveModal && aircraftData?.aircraft && (
          <SaveWBModal
            aircraftId={selectedAircraftId}
            aircraftLabel={[
              aircraftData.aircraft.registration,
              [aircraftData.aircraft.manufacturer, aircraftData.aircraft.model_name].filter(Boolean).join(' '),
            ].filter(Boolean).join(' — ')}
            inputs={apiInputs}
            currentResult={result}
            onClose={() => setShowSaveModal(false)}
            onSaved={() => { setShowSaveModal(false); }}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>

      {/* Modal Histórico */}
      <AnimatePresence>
        {showHistoryModal && (
          <WBHistoryModal
            aircraftId={selectedAircraftId || undefined}
            pilotId={currentUser?.id}
            onClose={() => setShowHistoryModal(false)}
            onLoadAsTemplate={handleLoadTemplate}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>

      {/* Toast cuando se carga un template */}
      <AnimatePresence>
        {templateToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              position: 'fixed',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 120,
              padding: '10px 16px',
              borderRadius: 12,
              background: 'rgba(34,197,94,0.95)',
              color: 'white',
              fontSize: 13,
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            }}
          >
            ✓ {t('wb_history_loaded_toast', { ns: 'admin' })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spin animation */}
      <style>{`
        @keyframes wbSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: wbSpin 1s linear infinite; }
        @media (max-width: 480px) {
          .wb-btn-label { display: none; }
        }
      `}</style>
    </motion.div>
  );
};

// ─── Subcomponentes ─────────────────────────────────────────────────
const Section = ({ title, children, darkMode }) => {
  const sub = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p
        style={{
          margin: 0,
          fontSize: 11,
          fontWeight: 700,
          color: sub,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {title}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
};

const NumField = ({ label, unit, value, onChange, onStep, step = 1, min, error, hint, darkMode }) => {
  const text = darkMode ? 'white' : '#0f172a';
  const sub = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const border = error
    ? '1px solid rgba(239,68,68,0.4)'
    : darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';
  const bg = darkMode ? 'rgba(255,255,255,0.04)' : 'white';

  const handleStep = (d) => onStep && onStep(d);

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: sub,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span>{label}</span>
        {hint && <span style={{ fontWeight: 500, fontSize: 11 }}>{hint}</span>}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          background: bg,
          border,
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          onClick={() => handleStep(-step)}
          style={stepBtn(darkMode)}
          aria-label="−"
        >
          −
        </button>
        <input
          type="number"
          inputMode="decimal"
          value={value}
          min={min}
          step={step}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: text,
            padding: '10px 8px',
            fontSize: 14,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            minWidth: 0,
            MozAppearance: 'textfield',
          }}
        />
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px',
            color: sub,
            fontSize: 12,
            fontWeight: 600,
            borderLeft: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
            background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.03)',
          }}
        >
          {unit}
        </span>
        <button
          type="button"
          onClick={() => handleStep(step)}
          style={stepBtn(darkMode)}
          aria-label="+"
        >
          +
        </button>
      </div>
      {error && (
        <span style={{ fontSize: 11, color: '#ef4444' }}>{error}</span>
      )}
    </label>
  );
};

function stepBtn(darkMode) {
  return {
    width: 36,
    border: 'none',
    background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)',
    color: darkMode ? 'rgba(255,255,255,0.7)' : '#475569',
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 700,
  };
}

function round1(n) {
  return Math.round(Number(n) * 10) / 10;
}

function formatFuelMax(liters, unit) {
  const lit = Number(liters);
  if (!Number.isFinite(lit)) return '—';
  if (unit === 'gal') return (lit * L_TO_GAL).toFixed(1);
  return lit.toFixed(0);
}

export default WBCalculator;
